// apps/accounts/src/pages/Partners.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Users, UserCheck, UserX, CheckCircle, XCircle,
  Clock, Eye, Pencil, Trash2, Plus, Search, RefreshCw,
  Shield, Building2, Link2, UserPlus
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../lib/pocketbaseClient';
import { cn } from '../lib/utils';

const DEFAULT_PASSWORD = 'Partner@123';

export default function Partners() {
  const [partners, setPartners] = useState([]);
  const [pendingPartners, setPendingPartners] = useState([]);
  const [approvedPartners, setApprovedPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [showModal, setShowModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({
    contactName: '',
    companyName: '',
    phone: '',
    email: '',
    address: '',
    partnerType: 'partner-construction',
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    fetchPartners();
    return () => { isMounted.current = false; };
  }, []);

  const fetchPartners = async () => {
    try {
      const records = await pb.collection('partners').getFullList({
        sort: 'name',
        expand: 'createdBy',
      });

      const users = await pb.collection('users').getFullList({
        filter: 'role ~ "partner-" || role = "client"',
      });

      const enrichedPartners = records.map(partner => {
        const user = users.find(u => u.id === partner.createdBy);
        return {
          ...partner,
          userStatus: user?.verified ? 'verified' : 'pending',
          userRole: user?.role || 'client',
          userEmail: user?.email || partner.email,
          userName: user?.name || partner.name,
          isVerified: user?.verified || false,
          userId: user?.id || null,
        };
      });

      if (isMounted.current) {
        setPartners(enrichedPartners);
        setPendingPartners(enrichedPartners.filter(p => !p.isVerified));
        setApprovedPartners(enrichedPartners.filter(p => p.isVerified));
      }
    } catch (err) {
      console.error('Fetch error:', err);
      toast.error('Failed to load partners');
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  // --- Approve Partner ------------------------------------------
  const handleApprove = async (partner) => {
    try {
      let users = [];
      if (partner.userId) {
        users = await pb.collection('users').getFullList({
          filter: `id = "${partner.userId}"`,
        });
      }
      if (users.length === 0 && partner.email) {
        users = await pb.collection('users').getFullList({
          filter: `email = "${partner.email}"`,
        });
      }
      
      if (users.length > 0) {
        const user = users[0];
        await pb.collection('users').update(user.id, {
          verified: true,
          role: partner.userRole || 'partner-construction',
        });
        
        toast.success(`${partner.name} has been approved!`);
        fetchPartners();
      } else {
        toast.error('User not found for this partner');
      }
    } catch (err) {
      console.error('Approval error:', err);
      toast.error('Failed to approve partner: ' + err.message);
    }
  };

  // --- Reject Partner -------------------------------------------
  const handleReject = async (partner) => {
    if (!window.confirm(`Reject ${partner.name}? This will delete their account.`)) return;
    
    try {
      let users = [];
      if (partner.userId) {
        users = await pb.collection('users').getFullList({
          filter: `id = "${partner.userId}"`,
        });
      }
      if (users.length === 0 && partner.email) {
        users = await pb.collection('users').getFullList({
          filter: `email = "${partner.email}"`,
        });
      }
      
      if (users.length > 0) {
        await pb.collection('users').delete(users[0].id);
      }
      
      await pb.collection('partners').delete(partner.id);
      
      toast.success(`${partner.name} has been rejected.`);
      fetchPartners();
    } catch (err) {
      console.error('Reject error:', err);
      toast.error('Failed to reject partner: ' + err.message);
    }
  };

  // --- Delete Partner -------------------------------------------
  const handleDelete = async (partner) => {
    if (!window.confirm(`Delete ${partner.name}? This action cannot be undone.`)) return;
    
    try {
      let users = [];
      if (partner.userId) {
        users = await pb.collection('users').getFullList({
          filter: `id = "${partner.userId}"`,
        });
      }
      if (users.length === 0 && partner.email) {
        users = await pb.collection('users').getFullList({
          filter: `email = "${partner.email}"`,
        });
      }
      
      if (users.length > 0) {
        await pb.collection('users').delete(users[0].id);
      }
      
      await pb.collection('partners').delete(partner.id);
      
      toast.success(`${partner.name} has been deleted.`);
      fetchPartners();
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete partner: ' + err.message);
    }
  };

  // --- Edit Partner ---------------------------------------------
  const handleEdit = (partner) => {
    setEditingPartner(partner);
    const user = partners.find(p => p.id === partner.id);
    setFormData({
      contactName: user?.userName || partner.name || '',
      companyName: partner.name || '',
      phone: partner.phone || '',
      email: partner.email || '',
      address: partner.address || '',
      partnerType: user?.userRole || 'partner-construction',
    });
    setShowModal(true);
  };

  // --- Create/Update Partner ----------------------------------
  const handleCreate = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      const data = { ...formData };
      Object.keys(data).forEach(k => { if (data[k] === '') data[k] = null; });

      const partnerName = data.companyName || data.contactName;

      // Check if email already exists
      const existingUsers = await pb.collection('users').getFullList({
        filter: `email = "${data.email}"`,
      });

      let userId = null;
      let isNewUser = false;

      if (existingUsers.length > 0) {
        userId = existingUsers[0].id;
        if (existingUsers[0].role !== data.partnerType) {
          await pb.collection('users').update(userId, {
            role: data.partnerType,
          });
        }
      } else {
        const newUser = await pb.collection('users').create({
          email: data.email,
          password: DEFAULT_PASSWORD,
          passwordConfirm: DEFAULT_PASSWORD,
          name: data.contactName,
          username: data.email.split('@')[0] + '_' + Date.now().toString().slice(-4),
          role: data.partnerType,
          verified: true,
          emailVisibility: true,
          phone: data.phone || null,
          address: data.address || null,
        });
        userId = newUser.id;
        isNewUser = true;
      }

      // Check for existing partner
      const existingPartners = await pb.collection('partners').getFullList({
        filter: `email = "${data.email}" || createdBy = "${userId}"`,
      });

      if (existingPartners.length > 0 && !editingPartner) {
        toast.error('Partner already exists with this email');
        setFormLoading(false);
        return;
      }

      const partnerData = {
        name: partnerName,
        phone: data.phone || '',
        email: data.email,
        address: data.address || '',
        status: 'pending',
        createdBy: userId,
      };

      if (editingPartner) {
        await pb.collection('partners').update(editingPartner.id, partnerData);
        toast.success('Partner updated successfully');
      } else {
        await pb.collection('partners').create(partnerData);
        toast.success(
          `Partner created! Default password: ${DEFAULT_PASSWORD}`,
          { duration: 10000 }
        );
      }

      handleCloseModal();
      fetchPartners();
    } catch (err) {
      console.error('Save error:', err);
      let msg = err.message || 'Failed to save partner';
      if (err.data?.data) {
        const fields = Object.entries(err.data.data)
          .map(([k, v]) => `${k}: ${v.message}`)
          .join('; ');
        msg += ` (${fields})`;
      }
      setFormError(msg);
      setFormLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPartner(null);
    setFormData({
      contactName: '',
      companyName: '',
      phone: '',
      email: '',
      address: '',
      partnerType: 'partner-construction',
    });
    setFormError('');
    setFormLoading(false);
  };

  // --- Filters --------------------------------------------------
  const filteredPartners = (list) => {
    return list.filter(p =>
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.phone?.includes(search) ||
      p.email?.toLowerCase().includes(search.toLowerCase())
    );
  };

  const getStatusBadge = (partner) => {
    if (!partner.isVerified) {
      return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Pending Approval</Badge>;
    }
    return <Badge className="bg-green-100 text-green-700 border-green-200">Verified</Badge>;
  };

  const getPartnerType = (partner) => {
    if (partner.userRole === 'partner-construction') {
      return <Badge className="bg-orange-100 text-orange-700 border-orange-200">Construction</Badge>;
    }
    if (partner.userRole === 'partner-interior') {
      return <Badge className="bg-purple-100 text-purple-700 border-purple-200">Interior</Badge>;
    }
    if (partner.userRole === 'client') {
      return <Badge className="bg-blue-100 text-blue-700 border-blue-200">Client</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-700 border-slate-200">Unknown</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Users className="w-6 h-6 text-amber-500" />
            Partners & Clients
            <Badge variant="outline" className="ml-2">{partners.length}</Badge>
          </h1>
          <p className="text-sm text-slate-500">Manage business partners and clients</p>
        </div>
        <Button className="bg-amber-500 hover:bg-amber-600" onClick={() => { setEditingPartner(null); setShowModal(true); }}>
          <Plus className="w-4 h-4 mr-2" /> New Entry
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 font-medium">Total</p>
            <p className="text-2xl font-bold text-slate-800">{partners.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 font-medium">Pending</p>
            <p className="text-2xl font-bold text-yellow-600">{pendingPartners.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 font-medium">Approved</p>
            <p className="text-2xl font-bold text-green-600">{approvedPartners.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 font-medium">Assigned Sites</p>
            <p className="text-2xl font-bold text-blue-600">
              {approvedPartners.filter(p => p.isVerified).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex flex-wrap items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-slate-100">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button variant="outline" onClick={() => setSearch('')}>Clear</Button>
        <Button variant="outline" onClick={fetchPartners}><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="pending" className="flex items-center gap-1">
            <Clock className="w-4 h-4" /> Pending ({pendingPartners.length})
          </TabsTrigger>
          <TabsTrigger value="approved" className="flex items-center gap-1">
            <CheckCircle className="w-4 h-4" /> Approved ({approvedPartners.length})
          </TabsTrigger>
        </TabsList>

        {/* Pending Tab */}
        <TabsContent value="pending" className="mt-4">
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Name</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Contact</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Type</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredPartners(pendingPartners).length === 0 ? (
                    <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-500">No pending entries.</td></tr>
                  ) : (
                    filteredPartners(pendingPartners).map(p => (
                      <tr key={p.id} className="hover:bg-slate-50/50 transition">
                        <td className="px-4 py-3 font-medium">{p.name}</td>
                        <td className="px-4 py-3">
                          <div className="text-sm">{p.phone || '-'}</div>
                          <div className="text-xs text-slate-400">{p.email || '-'}</div>
                        </td>
                        <td className="px-4 py-3">{getPartnerType(p)}</td>
                        <td className="px-4 py-3">{getStatusBadge(p)}</td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1 flex-wrap">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-1.5 h-auto text-green-600 hover:bg-green-50"
                              onClick={() => handleApprove(p)}
                              title="Approve"
                            >
                              <CheckCircle size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-1.5 h-auto text-red-600 hover:bg-red-50"
                              onClick={() => handleReject(p)}
                              title="Reject"
                            >
                              <XCircle size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-1.5 h-auto text-amber-600 hover:bg-amber-50"
                              onClick={() => handleEdit(p)}
                              title="Edit"
                            >
                              <Pencil size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-1.5 h-auto text-red-600 hover:bg-red-50"
                              onClick={() => handleDelete(p)}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Approved Tab */}
        <TabsContent value="approved" className="mt-4">
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Name</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Contact</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Type</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredPartners(approvedPartners).length === 0 ? (
                    <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-500">No approved entries found.</td></tr>
                  ) : (
                    filteredPartners(approvedPartners).map(p => (
                      <tr key={p.id} className="hover:bg-slate-50/50 transition">
                        <td className="px-4 py-3 font-medium">{p.name}</td>
                        <td className="px-4 py-3">
                          <div className="text-sm">{p.phone || '-'}</div>
                          <div className="text-xs text-slate-400">{p.email || '-'}</div>
                        </td>
                        <td className="px-4 py-3">{getPartnerType(p)}</td>
                        <td className="px-4 py-3">{getStatusBadge(p)}</td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1 flex-wrap">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-1.5 h-auto text-amber-600 hover:bg-amber-50"
                              onClick={() => handleEdit(p)}
                              title="Edit"
                            >
                              <Pencil size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="p-1.5 h-auto text-red-600 hover:bg-red-50"
                              onClick={() => handleDelete(p)}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold text-slate-800 mb-4">
              {editingPartner ? '?? Edit' : '? New Entry'}
            </h2>
            {formError && (
              <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-4 border border-red-200 whitespace-pre-wrap">
                {formError}
              </div>
            )}
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Contact Name *</label>
                <Input
                  name="contactName"
                  value={formData.contactName}
                  onChange={(e) => setFormData({...formData, contactName: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Company Name</label>
                <Input
                  name="companyName"
                  value={formData.companyName}
                  onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                  placeholder="Leave blank to use contact name"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                  <Input
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                <Input
                  name="address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                <select
                  name="partnerType"
                  value={formData.partnerType}
                  onChange={(e) => setFormData({...formData, partnerType: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400"
                >
                  <option value="partner-construction">Construction Partner</option>
                  <option value="partner-interior">Interior Partner</option>
                  <option value="client">Client</option>
                </select>
              </div>
              {!editingPartner && (
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-700">
                    ?? Default password: <strong>{DEFAULT_PASSWORD}</strong>
                  </p>
                  <p className="text-xs text-blue-500 mt-1">User can change password after first login.</p>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <Button type="button" variant="outline" onClick={handleCloseModal}>Cancel</Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-600" disabled={formLoading}>
                  {formLoading ? 'Saving...' : (editingPartner ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}